import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { addCategory, updateCategory } from '@/store/Category';

interface Props {
  onClose?: () => void;
  mode?: 'add' | 'edit';
  categoryId?: string;
  initialTitle?: string;
  onSuccess?: (title: string) => void;
}

const AddCategory: React.FC<Props> = ({
  onClose,
  mode = 'add',
  categoryId,
  initialTitle = '',
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const [title, setTitle] = useState(initialTitle);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditMode = mode === 'edit';

  const handleClose = () => {
    setTitle(initialTitle);
    onClose?.();
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      toast.info('Category title is required.');
      return;
    }

    try {
      setIsSubmitting(true);
      if (isEditMode && categoryId) {
        await dispatch(
          updateCategory({ categoryId, name: trimmedTitle })
        ).unwrap();
        toast.success('Category updated successfully');
      } else {
        await dispatch(addCategory({ name: trimmedTitle })).unwrap();
        toast.success('Category added successfully');
      }
      onSuccess?.(trimmedTitle);
      handleClose();
    } catch (error: any) {
      toast.error(
        error?.message ||
          `Failed to ${isEditMode ? 'update' : 'add'} category`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='mx-auto w-full max-w-[506px]'>
      <div className='w-full rounded-[28px] bg-white px-8 pb-8 pt-9'>
        <div className='text-center'>
          <h2 className='text-[39px] font-bold leading-[1.05] tracking-[-0.04em] text-[#202124]'>
            {isEditMode ? 'Edit Category' : 'Add Category'}
          </h2>
          <p className='mt-[12px] text-[14px] font-normal text-[#5E5E5E]'>
            {isEditMode
              ? 'Update category to organise your gamics'
              : 'Add new category to organise your gamics'}
          </p>
        </div>

        <div className='mt-[36px] pb-[8px]'>
          <input
            id='category-title'
            type='text'
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder='Enter category title'
            className='h-[65px] w-full rounded-[4px] border-0 border-b border-b-[#CFCFCF] bg-[#F5F5F5] px-4 pb-[10px] pt-[28px] text-[16px] text-[#212121] outline-none placeholder:text-[#C9C9C9] focus:border-b-[#B9B9B9]'
            autoFocus
          />
          <label
            htmlFor='category-title'
            className='pointer-events-none -mt-[55px] mb-[34px] block px-4 text-[14px] font-normal text-[#555555]'
          >
            Title
          </label>
        </div>

        <div className='mt-[38px] flex items-center gap-2.5'>
          <button
            type='button'
            onClick={handleClose}
            className='flex h-[56px] flex-1 items-center justify-center rounded-full border border-[#2F2F2F] bg-white px-6 text-[15px] font-normal text-[#232323] transition hover:bg-[#F8F8F8]'
          >
            No, Cancel
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={isSubmitting}
            className='flex h-[56px] flex-1 items-center justify-center rounded-full bg-[#ff0a54] px-6 text-[15px] font-normal text-white transition hover:bg-[#e6004b] disabled:cursor-not-allowed disabled:opacity-70'
          >
            {isSubmitting
              ? isEditMode
                ? 'Saving...'
                : 'Adding...'
              : isEditMode
                ? 'Yes, Save'
                : 'Yes, Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCategory;
